/**
 * User Authentication API Service
 * Clean, simple API integration using centralized configuration
 * Follows KISS principle - no manual URL construction
 */

import { User } from '../../../components/layout/Header/Header.types';
import { apiClient, userEndpoints, apiHelpers } from '../../../lib/api';
import { generateDeviceFingerprintData } from '../../../lib/utils/deviceFingerprint';
import { isCaptchaChallenge, type CaptchaChallenge } from '../../../lib/captcha';
import type { RegistrationConsents } from '../../legal-consent/types';

// Extended User interface for backend API responses
export interface UserApi extends User {
  isAnonymous: boolean;
  emailVerified?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

// Request/Response Types
export interface UserLoginRequest {
  email: string;
  password: string;
  /** hCaptcha token from completed challenge (optional - only needed if CAPTCHA was required) */
  hcaptchaToken?: string;
}

/**
 * Password-based registration request
 * Used when user registers with email + password (traditional flow)
 */
export interface UserRegisterRequest {
  email: string;
  password: string;
  name: string;
  isAnonymous?: boolean;
  /** hCaptcha token from completed challenge (optional - only needed if CAPTCHA was required) */
  hcaptchaToken?: string;
  /** Legal consent flags for GDPR compliance (required for new registrations) */
  consents?: RegistrationConsents;
}

/**
 * OTP-based registration request
 * Used when new user is detected during OTP flow - no password required
 * User will verify identity via OTP code
 */
export interface OtpRegisterRequest {
  email: string;
  firstName: string;
  lastName: string;
}

// Two-Factor Authentication Status Interface
export interface TwoFactorAuth {
  enabled: boolean;
  required: boolean;      // Key: tells UI if 2FA prompt needed
  method: 'email' | 'sms';
  deviceTrusted: boolean; // Key: device already trusted
}

export interface UserLoginSuccessResponse {
  success: true;
  data: {
    user: UserApi;
    tokens: {
      accessToken: string;
      refreshToken: string;
      expiresIn: number;
      tokenType: string;
    };
    twoFactorAuth: TwoFactorAuth; // NEW: Enhanced auth status
  };
  message: string;
  timestamp: string;
}

export interface UserLoginErrorResponse {
  success: false;
  message: string;
  timestamp: string;
}

/**
 * User Login Response
 * Includes CaptchaChallenge for proper TypeScript type narrowing with isCaptchaChallenge()
 */
export type UserLoginResponse = UserLoginSuccessResponse | UserLoginErrorResponse | CaptchaChallenge;

export interface UserRegisterSuccessResponse {
  success: boolean;
  data: {
    user: UserApi;
    tokens: {
      accessToken: string;
      refreshToken: string;
      expiresIn: number;
      tokenType: string;
    };
  };
  message: string;
  timestamp: string;
}

/**
 * User Register Response
 * Includes CaptchaChallenge for proper TypeScript type narrowing with isCaptchaChallenge()
 */
export type UserRegisterResponse = UserRegisterSuccessResponse | CaptchaChallenge;

/**
 * OTP-based registration response
 * Returns when user registers via OTP flow (no password)
 * User must verify OTP to complete registration
 */
export interface OtpRegisterSuccessResponse {
  success: true;
  data: {
    user: {
      id: string;
      email: string;
      status: 'PENDING_VERIFICATION';
    };
    requiresVerification: true;
  };
  message: string;
  timestamp: string;
}

export interface OtpRegisterErrorResponse {
  success: false;
  error: string;
  message?: string;
  timestamp: string;
}

export type OtpRegisterResponse = OtpRegisterSuccessResponse | OtpRegisterErrorResponse;

// ─────────────────────────────────────────────────────────────────────────────
// Phone OTP Types (Account Recovery)
// ─────────────────────────────────────────────────────────────────────────────

/** Request to send OTP to phone for account recovery */
export interface SendPhoneOtpRequest {
  /** User's email address (to identify the account) */
  email: string;
  /** Phone number to send OTP to (must be associated with account) */
  phone: string;
}

/** Success response when phone OTP is sent */
export interface SendPhoneOtpSuccessResponse {
  success: true;
  data: {
    /** Masked phone for display (e.g., "+1***123") */
    maskedPhone: string;
    /** Seconds until OTP expires (typically 300 = 5 minutes) */
    expiresIn: number;
  };
  message: string;
  timestamp: string;
}

/** Error response when phone OTP send fails */
export interface SendPhoneOtpErrorResponse {
  success: false;
  error: string;
  message?: string;
  timestamp: string;
}

export type SendPhoneOtpResponse = SendPhoneOtpSuccessResponse | SendPhoneOtpErrorResponse;

/** Request to verify phone OTP */
export interface VerifyPhoneOtpRequest {
  /** User's email address */
  email: string;
  /** Phone number that received the OTP */
  phone: string;
  /** 6-digit OTP code */
  code: string;
}

/** Success response when phone OTP is verified */
export interface VerifyPhoneOtpSuccessResponse {
  success: true;
  data: {
    user: {
      id: string;
      email: string;
      firstName?: string;
      lastName?: string;
      role: string;
      status: string;
    };
    tokens: {
      accessToken: string;
      refreshToken: string;
      expiresIn: number;
    };
  };
  message: string;
  timestamp: string;
}

/** Error response when phone OTP verification fails */
export interface VerifyPhoneOtpErrorResponse {
  success: false;
  error: string;
  message?: string;
  timestamp: string;
}

export type VerifyPhoneOtpResponse = VerifyPhoneOtpSuccessResponse | VerifyPhoneOtpErrorResponse;

// ─────────────────────────────────────────────────────────────────────────────
// Social Auth Types (Google, Apple)
// ─────────────────────────────────────────────────────────────────────────────

/** Request for social login (Google or Apple ID token) */
export interface SocialAuthRequest {
  provider: 'google' | 'apple';
  idToken: string;
  /** Google CSRF token from cookie (optional, for GSI) */
  g_csrf_token?: string;
  /** Apple nonce (optional, if used in auth request) */
  nonce?: string;
  /** Legal consent flags for GDPR compliance (optional -- existing users skip this) */
  consents?: RegistrationConsents;
}

/** Success response for social login */
export interface SocialAuthSuccessResponse {
  success: true;
  data: {
    user: UserApi;
    tokens: {
      accessToken: string;
      refreshToken: string;
      expiresIn: number;
      tokenType: string;
    };
  };
  message: string;
  timestamp: string;
}

/** Error response for social login */
export interface SocialAuthErrorResponse {
  success: false;
  error: string;
  message?: string;
  timestamp: string;
}

export type SocialAuthResponse = SocialAuthSuccessResponse | SocialAuthErrorResponse;

export interface UserRefreshResponse {
  success: boolean;
  data: {
    accessToken: string;
    refreshToken: string;
    expiresIn: number;
  };
  message: string;
}

export interface UserProfileResponse {
  success: boolean;
  data: UserApi;
  message: string;
}

export interface UserAnonymousResponse {
  success: boolean;
  data: {
    user: UserApi;
    tokens: {
      accessToken: string;
      refreshToken: string;
      expiresIn: number;
      tokenType: string;
    };
  };
  message: string;
  timestamp: string;
}

export interface UserVerifyEmailRequest {
  token: string;
}

export interface UserVerifyEmailResponse {
  success: boolean;
  message: string;
  timestamp: string;
}

// ─────────────────────────────────────────────────────────────────────────────
// Email Verification Status Types
// ─────────────────────────────────────────────────────────────────────────────

/** Response for checking email verification status */
export interface EmailVerificationStatusResponse {
  success: boolean;
  data: {
    /** Whether the email has been verified */
    isVerified: boolean;
    /** User's email address */
    email: string;
    /** When the email was verified (ISO timestamp) */
    verifiedAt?: string;
    /** Whether user can request a new verification email */
    canResend: boolean;
    /** Seconds until user can request another verification email */
    resendCooldownSeconds?: number;
  };
  message: string;
  timestamp: string;
}

/** Request to resend verification email */
export interface ResendVerificationEmailRequest {
  email: string;
}

/** Response for resending verification email */
export interface ResendVerificationEmailResponse {
  success: boolean;
  data?: {
    /** Seconds until user can request another verification email */
    cooldownSeconds: number;
  };
  message: string;
  timestamp: string;
}

export interface UserForgotPasswordRequest {
  identifier: string;
  identifierType: 'email' | 'phone';
  /** hCaptcha token from completed challenge (optional - only needed if CAPTCHA was required) */
  hcaptchaToken?: string;
}

export interface UserForgotPasswordSuccessResponse {
  success: boolean;
  message: string;
  timestamp: string;
}

/**
 * Forgot Password Response
 * Can be success or CAPTCHA challenge (202/400 status codes)
 */
export type UserForgotPasswordResponse = UserForgotPasswordSuccessResponse | CaptchaChallenge;

export interface UserVerifyResetCodeRequest {
  identifier: string;
  identifierType: 'email' | 'phone';
  code: string;
}

export interface UserVerifyResetCodeResponse {
  success: boolean;
  data: {
    resetToken: string;
    expiresAt: string;
  };
  message: string;
  timestamp: string;
}

export interface UserResetPasswordRequest {
  identifier: string;
  identifierType: 'email' | 'phone';
  code: string;
  newPassword: string;
}

export interface UserResetPasswordResponse {
  success: boolean;
  message: string;
  timestamp: string;
}

export interface UserVerifyTwoFactorRequest {
  code?: string;           // 6-digit OTP code (required unless backupCode provided)
  backupCode?: string;     // 8-character backup code (alternative to OTP)
  rememberDevice?: boolean;
  sessionId?: string;      // From pending login data
}

export interface UserVerifyTwoFactorResponse {
  success: boolean;
  data?: {
    user: UserApi;
    tokens: {
      accessToken: string;
      refreshToken: string;
      expiresIn: number;
      tokenType: string;
    };
    twoFactorAuth: TwoFactorAuth;
  };
  message: string;
  timestamp: string;
}

export interface UserResendTwoFactorRequest {
  sessionId: string;
  /** hCaptcha token from completed challenge (optional - only needed if CAPTCHA was required) */
  hcaptchaToken?: string;
}

export interface UserResendTwoFactorSuccessResponse {
  success: boolean;
  message: string;
  timestamp: string;
}

/**
 * User Resend Two-Factor Response
 * Includes CaptchaChallenge for proper TypeScript type narrowing with isCaptchaChallenge()
 */
export type UserResendTwoFactorResponse = UserResendTwoFactorSuccessResponse | CaptchaChallenge;

// Device Management Interfaces (for Phase 2)
export interface TrustedDevice {
  id: string;
  fingerprint: string;
  userAgent: string;
  ipAddress: string;
  lastUsed: string;
  trustedAt: string;
  expiresAt: string;
}

export interface TrustedDevicesResponse {
  success: boolean;
  data: TrustedDevice[];
  message: string;
  timestamp: string;
}

export interface RevokeDeviceTrustRequest {
  deviceFingerprint: string;
}

export interface RevokeDeviceTrustResponse {
  success: boolean;
  message: string;
  timestamp: string;
}

// CAPTCHA Challenge Response Types
export interface CaptchaChallengeResponse {
  success: false;
  error: 'CAPTCHA_CHALLENGE_REQUIRED' | 'CAPTCHA_REQUIRED' | 'CAPTCHA_VERIFICATION_FAILED';
  captchaRequired: true;
  captchaMode: 'invisible' | 'visible';
  siteKey: string;
  riskRequestId: string;
  message: string;
  timestamp: string;
}

// Error Types
export interface UserApiError {
  success: false;
  message: string;
  timestamp: string;
}

// ─────────────────────────────────────────────────────────────────────────────
// Email OTP Types (Primary Auth Method)
// ─────────────────────────────────────────────────────────────────────────────

export interface SendEmailOtpRequest {
  email: string;
  /** First name for new user registration (second call with names creates account) */
  firstName?: string;
  /** Last name for new user registration (second call with names creates account) */
  lastName?: string;
  /** hCaptcha token from completed challenge (optional - only needed if CAPTCHA was required) */
  hcaptchaToken?: string;
  /** Legal consent flags for GDPR compliance (required when firstName + lastName present) */
  consents?: RegistrationConsents;
}

export interface SendEmailOtpSuccessResponse {
  success: true;
  data: {
    /** Masked email for display (e.g., "o***@g***.com") */
    maskedEmail: string;
    /** Seconds until OTP expires (typically 600 = 10 minutes) */
    expiresIn: number;
    /** True if this email is not yet registered */
    isNewUser: boolean;
    /** Job ID for tracking email validation status via SSE (only for unknown domains) */
    validationJobId?: string;
  };
  message: string;
  timestamp: string;
}

// ─────────────────────────────────────────────────────────────────────────────
// Email Validation Status Types (SSE-based real-time updates)
// ─────────────────────────────────────────────────────────────────────────────

/** Possible states for email validation via SSE */
export type EmailValidationStatus = 'validating' | 'valid' | 'invalid' | 'timeout';

/** SSE event data for email validation status updates */
export interface EmailValidationStatusEvent {
  /** Current validation status */
  status: EmailValidationStatus;
  /** Reason for invalid status (e.g., "mailbox does not exist") */
  reason?: string;
}

export type SendEmailOtpResponse = SendEmailOtpSuccessResponse | CaptchaChallenge;

export interface VerifyEmailOtpRequest {
  email: string;
  code: string;
}

export interface VerifyEmailOtpSuccessResponse {
  success: true;
  data: {
    user: UserApi;
    tokens: {
      accessToken: string;
      refreshToken: string;
      expiresIn: number;
      tokenType: string;
    };
    isNewUser: boolean;
  };
  message: string;
  timestamp: string;
}

export type VerifyEmailOtpResponse = VerifyEmailOtpSuccessResponse | UserApiError;

// ─────────────────────────────────────────────────────────────────────────────
// Passkey/WebAuthn Types (Secondary Auth Method)
// ─────────────────────────────────────────────────────────────────────────────

import type {
  PublicKeyCredentialCreationOptionsJSON,
  PublicKeyCredentialRequestOptionsJSON,
  RegistrationResponseJSON,
  AuthenticationResponseJSON,
} from '@simplewebauthn/browser';

export interface PasskeyLoginOptionsRequest {
  email: string;
}

export interface PasskeyLoginOptionsResponse {
  success: true;
  data: PublicKeyCredentialRequestOptionsJSON;
  message: string;
  timestamp: string;
}

export interface PasskeyLoginVerifyRequest {
  email: string;
  authenticationResponse: AuthenticationResponseJSON;
}

export interface PasskeyLoginVerifySuccessResponse {
  success: true;
  data: {
    user: UserApi;
    tokens: {
      accessToken: string;
      refreshToken: string;
      expiresIn: number;
      tokenType: string;
    };
  };
  message: string;
  timestamp: string;
}

export type PasskeyLoginVerifyResponse = PasskeyLoginVerifySuccessResponse | UserApiError;

export interface PasskeyRegistrationOptionsResponse {
  success: true;
  data: PublicKeyCredentialCreationOptionsJSON;
  message: string;
  timestamp: string;
}

export interface PasskeyRegistrationVerifyRequest {
  attestationResponse: RegistrationResponseJSON;
  friendlyName: string;
}

export interface PasskeyRegistrationVerifyResponse {
  success: true;
  data: {
    credentialId: string;
    friendlyName: string;
    createdAt: string;
  };
  message: string;
  timestamp: string;
}

export interface Passkey {
  id: string;
  credentialId: string;
  friendlyName: string;
  lastUsed: string | null;
  createdAt: string;
}

export interface ListPasskeysResponse {
  success: true;
  data: Passkey[];
  message: string;
  timestamp: string;
}

export interface DeletePasskeyResponse {
  success: true;
  message: string;
  timestamp: string;
}

export interface RenamePasskeyRequest {
  friendlyName: string;
}

export interface RenamePasskeyResponse {
  success: true;
  data: Passkey;
  message: string;
  timestamp: string;
}

/**
 * Error handling for user authentication
 */
function handleApiError(error: unknown): never {
  if (error instanceof Error) {
    throw error;
  }

  if (error && typeof error === 'object' && 'message' in error) {
    throw new Error(String((error as Record<string, unknown>).message));
  }

  throw new Error('Network error occurred. Please check your connection and try again.');
}

/**
 * User Login API
 * Authenticates user with email and password
 * Includes enhanced device fingerprinting for security
 */
export async function userLogin(credentials: UserLoginRequest): Promise<UserLoginResponse> {
  try {
    // Generate comprehensive device fingerprinting data
    const deviceFingerprintData = generateDeviceFingerprintData();

    // Validate fingerprint data has required UUID
    if (!deviceFingerprintData.fingerprint || typeof deviceFingerprintData.fingerprint !== 'string') {
      console.error('🔐 User Login: Invalid fingerprint data - missing UUID');
      throw new Error('Device fingerprinting failed - missing UUID');
    }

    // Prepare enhanced headers for device fingerprinting and incognito detection
    const enhancedHeaders = {
      'X-Device-Fingerprint': JSON.stringify(deviceFingerprintData),
      'X-Incognito-Detected': deviceFingerprintData.incognitoDetected.toString(),
      'X-Canvas-Fingerprint': deviceFingerprintData.canvasFingerprint,
      'X-Device-Fingerprint-Version': deviceFingerprintData.fingerprintVersion,
      'X-LocalStorage-Available': deviceFingerprintData.localStorageAvailable.toString(),
      'X-SessionStorage-Available': deviceFingerprintData.sessionStorageAvailable.toString(),
      'X-IndexedDB-Available': deviceFingerprintData.indexedDBAvailable.toString(),
    };

    console.log('🔐 User Login: Sending enhanced device fingerprinting data');
    console.log('🔐 Incognito Detection:', deviceFingerprintData.incognitoDetected);
    console.log('🔐 Device Fingerprint UUID:', deviceFingerprintData.fingerprint);

    const apiResponse = await apiClient.post<UserLoginResponse>(
      userEndpoints.auth.login(),
      credentials,
      { headers: enhancedHeaders }
    );

    // Check if API client returned a structured error response (including CAPTCHA challenges)
    if (apiResponse.success === false && apiResponse.data) {
      return apiResponse.data;
    }

    // For successful responses, API client wraps the data
    const response = apiResponse.data;

    if (!response) {
      throw new Error('No response data received');
    }

    // Check if response is a CAPTCHA challenge (risk-based security)
    if (isCaptchaChallenge(response)) {
      return response;
    }

    if (response.success === true) {
      return response;
    }

    throw new Error('Unexpected response format');
  } catch (error) {
    // If it's already a UserApiError, re-throw it
    if (error && typeof error === 'object' && 'success' in error && error.success === false) {
      throw error;
    }

    handleApiError(error);
  }
}

/**
 * User Register API
 * Creates new user account with device fingerprinting
 */
export async function userRegister(userData: UserRegisterRequest): Promise<UserRegisterResponse> {
  try {
    // Generate comprehensive device fingerprinting data for registration
    const deviceFingerprintData = generateDeviceFingerprintData();

    // Validate fingerprint data has required UUID
    if (!deviceFingerprintData.fingerprint || typeof deviceFingerprintData.fingerprint !== 'string') {
      console.error('🔐 User Register: Invalid fingerprint data - missing UUID');
      throw new Error('Device fingerprinting failed - missing UUID');
    }

    // Prepare enhanced headers for device fingerprinting - SAME as login and 2FA
    const enhancedHeaders = {
      'X-Device-Fingerprint': JSON.stringify(deviceFingerprintData),
      'X-Incognito-Detected': deviceFingerprintData.incognitoDetected.toString(),
      'X-Canvas-Fingerprint': deviceFingerprintData.canvasFingerprint,
      'X-Device-Fingerprint-Version': deviceFingerprintData.fingerprintVersion,
      'X-LocalStorage-Available': deviceFingerprintData.localStorageAvailable.toString(),
      'X-SessionStorage-Available': deviceFingerprintData.sessionStorageAvailable.toString(),
      'X-IndexedDB-Available': deviceFingerprintData.indexedDBAvailable.toString(),
    };

    console.log('🔐 User Register: Sending enhanced device fingerprinting data');
    console.log('🔐 Incognito Detection:', deviceFingerprintData.incognitoDetected);
    console.log('🔐 Device Fingerprint UUID:', deviceFingerprintData.fingerprint);

    const apiResponse = await apiClient.post<UserRegisterResponse>(
      userEndpoints.auth.register(),
      userData,
      { headers: enhancedHeaders }
    );

    // Check if API client returned a CAPTCHA challenge or structured error response
    if (apiResponse.success === false && apiResponse.data) {
      // Return the structured response (could be CAPTCHA challenge or error)
      return apiResponse.data as UserRegisterResponse;
    }

    const response = apiResponse.data;

    if (!response) {
      throw new Error('No response data received');
    }

    return response;
  } catch (error) {
    handleApiError(error);
  }
}

/**
 * User Logout API
 * Logs out the current user
 */
export async function userLogout(accessToken: string): Promise<void> {
  try {
    await apiClient.post(
      userEndpoints.auth.logout(),
      undefined,
      apiHelpers.withAuth(accessToken)
    );
  } catch (error) {
    handleApiError(error);
  }
}

/**
 * Refresh User Access Token
 * Uses refresh token to get new access token
 * Matches backend API guide specification exactly
 */
export async function refreshUserToken(refreshToken: string): Promise<UserRefreshResponse> {
  try {
    console.log('🔄 User Auth: Attempting token refresh...');

    const response = await apiClient.post<{
      success: boolean;
      data: {
        user: UserApi;
        tokens: {
          accessToken: string;
          refreshToken: string;
          expiresIn: number;
          tokenType: string;
        };
      };
      message: string;
      timestamp: string;
    }>(
      userEndpoints.auth.refresh(),
      { refreshToken }
    );

    console.log('🔄 User Auth: Refresh API response:', { 
      success: response.success, 
      hasData: !!response.data,
      message: response.message 
    });

    if (response.success && response.data && response.data.data && response.data.data.tokens) {
      // Extract tokens from the full response structure (note: nested data)
      const { accessToken, refreshToken: newRefreshToken, expiresIn } = response.data.data.tokens;
      
      return {
        success: true,
        data: {
          accessToken,
          refreshToken: newRefreshToken,
          expiresIn,
        },
        message: response.message || 'Token refreshed successfully',
      };
    }

    console.log('❌ User Auth: Refresh failed - invalid response structure');
    throw new Error(response.message || 'Token refresh failed - invalid response');
  } catch (error) {
    console.error('❌ User Auth: Refresh API error:', error);
    handleApiError(error);
  }
}

/**
 * Get User Profile
 * Retrieves current user profile
 */
export async function getUserProfile(accessToken: string): Promise<UserProfileResponse> {
  try {
    console.log('🔐 User Profile: Getting user profile...');

    const response = await apiClient.get<UserApi>(
      userEndpoints.auth.profile(),
      undefined,
      apiHelpers.withAuth(accessToken)
    );

    console.log('🔐 User Profile: API Response received:', response);

    if (response.success && response.data) {
      return {
        success: true,
        data: response.data,
        message: response.message || 'Profile retrieved successfully',
      };
    }

    throw new Error(response.message || 'Profile retrieval failed');
  } catch (error) {
    handleApiError(error);
  }
}

/**
 * Create Anonymous Session
 * Creates anonymous user session for donations with device fingerprinting
 */
export async function createAnonymousSession(): Promise<UserAnonymousResponse> {
  try {
    // Generate comprehensive device fingerprinting data for anonymous sessions
    const deviceFingerprintData = generateDeviceFingerprintData();

    // Validate fingerprint data has required UUID
    if (!deviceFingerprintData.fingerprint || typeof deviceFingerprintData.fingerprint !== 'string') {
      console.error('🔐 Anonymous Session: Invalid fingerprint data - missing UUID');
      throw new Error('Device fingerprinting failed - missing UUID');
    }

    // Prepare enhanced headers for device fingerprinting - SAME as login, register, and 2FA
    const enhancedHeaders = {
      'X-Device-Fingerprint': JSON.stringify(deviceFingerprintData),
      'X-Incognito-Detected': deviceFingerprintData.incognitoDetected.toString(),
      'X-Canvas-Fingerprint': deviceFingerprintData.canvasFingerprint,
      'X-Device-Fingerprint-Version': deviceFingerprintData.fingerprintVersion,
      'X-LocalStorage-Available': deviceFingerprintData.localStorageAvailable.toString(),
      'X-SessionStorage-Available': deviceFingerprintData.sessionStorageAvailable.toString(),
      'X-IndexedDB-Available': deviceFingerprintData.indexedDBAvailable.toString(),
    };

    console.log('🔐 Anonymous Session: Sending enhanced device fingerprinting data');
    console.log('🔐 Incognito Detection:', deviceFingerprintData.incognitoDetected);
    console.log('🔐 Device Fingerprint UUID:', deviceFingerprintData.fingerprint);

    const apiResponse = await apiClient.post<UserAnonymousResponse>(
      userEndpoints.auth.anonymous(),
      undefined,
      { headers: enhancedHeaders }
    );

    const response = apiResponse.data;

    if (!response) {
      throw new Error('No response data received');
    }

    return response;
  } catch (error) {
    handleApiError(error);
  }
}

/**
 * Verify Email
 * Verifies user email with verification token
 */
export async function verifyUserEmail(request: UserVerifyEmailRequest): Promise<UserVerifyEmailResponse> {
  try {
    const apiResponse = await apiClient.post<UserVerifyEmailResponse>(
      userEndpoints.auth.verifyEmail(),
      request
    );

    const response = apiResponse.data;

    if (!response) {
      throw new Error('No response data received');
    }

    return response;
  } catch (error) {
    handleApiError(error);
  }
}

/**
 * Get Email Verification Status
 * Checks whether the user's email has been verified
 * Also returns information about resend cooldown
 */
export async function getEmailVerificationStatus(): Promise<EmailVerificationStatusResponse> {
  try {
    const apiResponse = await apiClient.get<EmailVerificationStatusResponse>(
      userEndpoints.auth.emailVerificationStatus()
    );

    const response = apiResponse.data;

    if (!response) {
      throw new Error('No response data received');
    }

    return response;
  } catch (error) {
    handleApiError(error);
  }
}

/**
 * Resend Verification Email
 * Requests a new verification email to be sent
 * Subject to rate limiting (cooldown period between requests)
 */
export async function resendVerificationEmail(request: ResendVerificationEmailRequest): Promise<ResendVerificationEmailResponse> {
  try {
    const apiResponse = await apiClient.post<ResendVerificationEmailResponse>(
      userEndpoints.auth.resendVerificationEmail(),
      request
    );

    // Check if API client returned an error response
    if (apiResponse.success === false && apiResponse.data) {
      return apiResponse.data as ResendVerificationEmailResponse;
    }

    const response = apiResponse.data;

    if (!response) {
      throw new Error('No response data received');
    }

    return response;
  } catch (error) {
    handleApiError(error);
  }
}

/**
 * Forgot Password
 * Requests password reset email
 * Returns CAPTCHA challenge if risk assessment requires it
 */
export async function forgotUserPassword(request: UserForgotPasswordRequest): Promise<UserForgotPasswordResponse> {
  try {
    const apiResponse = await apiClient.post<UserForgotPasswordResponse>(
      userEndpoints.auth.forgotPassword(),
      request
    );

    // Check if API client returned a CAPTCHA challenge or structured error response
    if (apiResponse.success === false && apiResponse.data) {
      // Return the structured response (could be CAPTCHA challenge or error)
      return apiResponse.data as UserForgotPasswordResponse;
    }

    const response = apiResponse.data;

    if (!response) {
      throw new Error('No response data received');
    }

    return response;
  } catch (error) {
    handleApiError(error);
  }
}

/**
 * Verify Reset Code
 * Verifies the 6-digit code sent to user's email for password reset
 */
export async function verifyUserResetCode(request: UserVerifyResetCodeRequest): Promise<UserVerifyResetCodeResponse> {
  try {
    const apiResponse = await apiClient.post<UserVerifyResetCodeResponse>(
      userEndpoints.auth.verifyResetCode(),
      request
    );

    const response = apiResponse.data;

    if (!response) {
      throw new Error('No response data received');
    }

    // Check for API-level errors (validation failures from backend)
    if (!response.success) {
      throw new Error(response.message || 'Code verification failed');
    }

    return response;
  } catch (error) {
    handleApiError(error);
  }
}

/**
 * Reset Password
 * Resets password with reset token
 */
export async function resetUserPassword(request: UserResetPasswordRequest): Promise<UserResetPasswordResponse> {
  try {
    const apiResponse = await apiClient.post<UserResetPasswordResponse>(
      userEndpoints.auth.resetPassword(),
      request
    );

    const response = apiResponse.data;

    if (!response) {
      throw new Error('No response data received');
    }

    // Check for API-level errors (validation failures from backend)
    if (!response.success) {
      throw new Error(response.message || 'Password reset failed');
    }

    return response;
  } catch (error) {
    handleApiError(error);
  }
}

/**
 * Verify Two-Factor Authentication
 * Verifies the 2FA code and completes the login process
 */
export async function verifyUserTwoFactor(request: UserVerifyTwoFactorRequest): Promise<UserVerifyTwoFactorResponse> {
  try {
    // Get the pending login data from sessionStorage
    const pendingLoginData = sessionStorage.getItem('amalq_pending_login');
    if (!pendingLoginData) {
      throw new Error('No pending login data found. Please login again.');
    }

    const pendingLogin = JSON.parse(pendingLoginData);

    // Debug: Log what we're retrieving
    console.log('🔐 2FA API: Retrieved pending login data:', pendingLogin);
    console.log('🔐 2FA API: Tokens in pending data:', pendingLogin.tokens);
    console.log('🔐 2FA API: SessionId from tokens:', pendingLogin.tokens?.sessionId);
    console.log('🔐 2FA API: SessionId from authPolicy:', pendingLogin.authPolicy?.sessionId);

    // Generate the SAME device fingerprinting data as used in login
    // This ensures consistency between login and 2FA verification
    const deviceFingerprintData = generateDeviceFingerprintData();

    // Validate fingerprint data has required UUID
    if (!deviceFingerprintData.fingerprint || typeof deviceFingerprintData.fingerprint !== 'string') {
      console.error('🔐 2FA API: Invalid fingerprint data - missing UUID');
      throw new Error('Device fingerprinting failed - missing UUID');
    }

    // Prepare enhanced headers for device fingerprinting - SAME as login
    const enhancedHeaders = {
      'X-Device-Fingerprint': JSON.stringify(deviceFingerprintData),
      'X-Incognito-Detected': deviceFingerprintData.incognitoDetected.toString(),
      'X-Canvas-Fingerprint': deviceFingerprintData.canvasFingerprint,
      'X-Device-Fingerprint-Version': deviceFingerprintData.fingerprintVersion,
      'X-LocalStorage-Available': deviceFingerprintData.localStorageAvailable.toString(),
      'X-SessionStorage-Available': deviceFingerprintData.sessionStorageAvailable.toString(),
      'X-IndexedDB-Available': deviceFingerprintData.indexedDBAvailable.toString(),
    };

    // Prepare the verification request
    const extractedSessionId = pendingLogin.tokens?.sessionId || pendingLogin.authPolicy?.sessionId;
    console.log('🔐 2FA API: Using sessionId:', extractedSessionId);
    console.log('🔐 2FA API: Sending consistent device fingerprinting data');
    console.log('🔐 2FA API: Incognito Detection:', deviceFingerprintData.incognitoDetected);
    console.log('🔐 2FA API: Device Fingerprint UUID:', deviceFingerprintData.fingerprint);

    // Build verification request - supports both OTP code and backup code
    const verificationRequest: Record<string, unknown> = {
      rememberDevice: request.rememberDevice || false,
      sessionId: extractedSessionId,
      // Include other necessary data from pending login
      userId: pendingLogin.user?.id,
      method: pendingLogin.twoFactorAuth?.method,
    };

    // Send either code or backupCode (mutually exclusive)
    if (request.backupCode) {
      verificationRequest.backupCode = request.backupCode;
      console.log('🔐 2FA API: Using backup code for verification');
    } else if (request.code) {
      verificationRequest.code = request.code;
    }

    console.log('🔐 2FA API: Verification request:', verificationRequest);

    const apiResponse = await apiClient.post<UserVerifyTwoFactorResponse>(
      userEndpoints.auth.verifyTwoFactor(),
      verificationRequest,
      { headers: enhancedHeaders } // Add the SAME device fingerprinting headers as login
    );

    const response = apiResponse.data;

    if (!response) {
      throw new Error('No response data received');
    }

    return response;
  } catch (error) {
    handleApiError(error);
  }
}

/**
 * Resend Two-Factor Authentication Code
 * Requests a new 2FA code to be sent to the user's email
 * Returns CAPTCHA challenge if risk assessment requires it
 */
export async function resendUserTwoFactor(request?: UserResendTwoFactorRequest): Promise<UserResendTwoFactorResponse> {
  try {
    // Get the pending login data from sessionStorage
    const pendingLoginData = sessionStorage.getItem('amalq_pending_login');
    if (!pendingLoginData) {
      throw new Error('No pending login data found. Please login again.');
    }

    const pendingLogin = JSON.parse(pendingLoginData);

    // Use provided sessionId or extract from pending login data
    const sessionId = request?.sessionId || pendingLogin.tokens?.sessionId || pendingLogin.authPolicy?.sessionId;

    if (!sessionId) {
      throw new Error('No session ID available. Please login again.');
    }

    // Prepare the resend request with optional hCaptcha token
    const resendRequest = {
      sessionId,
      userId: pendingLogin.user?.id,
      method: pendingLogin.twoFactorAuth?.method,
      hcaptchaToken: request?.hcaptchaToken,
    };

    const apiResponse = await apiClient.post<UserResendTwoFactorResponse>(
      userEndpoints.auth.resendTwoFactor(),
      resendRequest
    );

    // Check if API client returned a CAPTCHA challenge or structured error response
    if (apiResponse.success === false && apiResponse.data) {
      return apiResponse.data as UserResendTwoFactorResponse;
    }

    const response = apiResponse.data;

    if (!response) {
      throw new Error('No response data received');
    }

    // Check if response is a CAPTCHA challenge (risk-based security)
    if (isCaptchaChallenge(response)) {
      return response;
    }

    return response;
  } catch (error) {
    handleApiError(error);
  }
}

/**
 * Validate User Token
 * Checks if the current access token is still valid
 * Simple boolean check - no manual error handling needed
 */
export async function validateUserToken(accessToken: string): Promise<boolean> {
  try {
    await getUserProfile(accessToken);
    return true;
  } catch (error) {
    return false;
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Email OTP API Methods (Primary Auth)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Send Email OTP
 * Sends a 6-digit OTP code to the user's email address
 * Works for both new users (registration) and existing users (login)
 * Returns CAPTCHA challenge if risk assessment requires it
 */
export async function sendEmailOtp(request: SendEmailOtpRequest): Promise<SendEmailOtpResponse> {
  try {
    // Generate device fingerprinting data for risk assessment
    const deviceFingerprintData = generateDeviceFingerprintData();

    // Validate fingerprint data has required UUID
    if (!deviceFingerprintData.fingerprint || typeof deviceFingerprintData.fingerprint !== 'string') {
      console.error('🔐 Send OTP: Invalid fingerprint data - missing UUID');
      throw new Error('Device fingerprinting failed - missing UUID');
    }

    // Prepare enhanced headers for device fingerprinting
    const enhancedHeaders = {
      'X-Device-Fingerprint': JSON.stringify(deviceFingerprintData),
      'X-Incognito-Detected': deviceFingerprintData.incognitoDetected.toString(),
      'X-Canvas-Fingerprint': deviceFingerprintData.canvasFingerprint,
      'X-Device-Fingerprint-Version': deviceFingerprintData.fingerprintVersion,
      'X-LocalStorage-Available': deviceFingerprintData.localStorageAvailable.toString(),
      'X-SessionStorage-Available': deviceFingerprintData.sessionStorageAvailable.toString(),
      'X-IndexedDB-Available': deviceFingerprintData.indexedDBAvailable.toString(),
    };

    console.log('🔐 Send OTP: Sending request with device fingerprinting');

    const apiResponse = await apiClient.post<SendEmailOtpResponse>(
      userEndpoints.auth.otpSend(),
      request,
      { headers: enhancedHeaders }
    );

    // Check if API client returned a CAPTCHA challenge
    if (apiResponse.success === false && apiResponse.data) {
      return apiResponse.data;
    }

    const response = apiResponse.data;

    if (!response) {
      throw new Error('No response data received');
    }

    // Check if response is a CAPTCHA challenge
    if (isCaptchaChallenge(response)) {
      return response;
    }

    return response;
  } catch (error) {
    handleApiError(error);
  }
}

/**
 * Register New User via OTP Flow
 * Called when isNewUser: true from OTP send
 * Creates user with PENDING_VERIFICATION status
 * User must verify OTP to complete registration and get tokens
 */
export async function registerOtpUser(request: OtpRegisterRequest): Promise<OtpRegisterResponse> {
  try {
    console.log('🔐 OTP Register: Creating new user account for', request.email);

    const apiResponse = await apiClient.post<OtpRegisterResponse>(
      userEndpoints.auth.register(),
      request
    );

    // Check if API client returned an error response
    if (apiResponse.success === false && apiResponse.data) {
      return apiResponse.data as OtpRegisterResponse;
    }

    const response = apiResponse.data;

    if (!response) {
      throw new Error('No response data received');
    }

    return response;
  } catch (error) {
    handleApiError(error);
  }
}

/**
 * Verify Email OTP
 * Verifies the 6-digit code and returns tokens on success
 * Handles both login (existing user) and registration (new user) flows
 */
export async function verifyEmailOtp(request: VerifyEmailOtpRequest): Promise<VerifyEmailOtpResponse> {
  try {
    // Generate device fingerprinting data for security
    const deviceFingerprintData = generateDeviceFingerprintData();

    // Validate fingerprint data has required UUID
    if (!deviceFingerprintData.fingerprint || typeof deviceFingerprintData.fingerprint !== 'string') {
      console.error('🔐 Verify OTP: Invalid fingerprint data - missing UUID');
      throw new Error('Device fingerprinting failed - missing UUID');
    }

    // Prepare enhanced headers for device fingerprinting
    const enhancedHeaders = {
      'X-Device-Fingerprint': JSON.stringify(deviceFingerprintData),
      'X-Incognito-Detected': deviceFingerprintData.incognitoDetected.toString(),
      'X-Canvas-Fingerprint': deviceFingerprintData.canvasFingerprint,
      'X-Device-Fingerprint-Version': deviceFingerprintData.fingerprintVersion,
      'X-LocalStorage-Available': deviceFingerprintData.localStorageAvailable.toString(),
      'X-SessionStorage-Available': deviceFingerprintData.sessionStorageAvailable.toString(),
      'X-IndexedDB-Available': deviceFingerprintData.indexedDBAvailable.toString(),
    };

    console.log('🔐 Verify OTP: Verifying code with device fingerprinting');

    const apiResponse = await apiClient.post<VerifyEmailOtpResponse>(
      userEndpoints.auth.otpVerify(),
      request,
      { headers: enhancedHeaders }
    );

    const response = apiResponse.data;

    if (!response) {
      throw new Error('No response data received');
    }

    return response;
  } catch (error) {
    handleApiError(error);
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Passkey/WebAuthn API Methods (Secondary Auth)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Get Passkey Login Options
 * Retrieves WebAuthn authentication challenge options for login
 * User must have registered at least one passkey
 */
export async function getPasskeyLoginOptions(
  email: string
): Promise<PublicKeyCredentialRequestOptionsJSON> {
  try {
    console.log('🔐 Passkey Login: Getting authentication options for', email);

    const apiResponse = await apiClient.post<PasskeyLoginOptionsResponse>(
      userEndpoints.auth.passkeyLoginOptions(),
      { email }
    );

    const response = apiResponse.data;

    if (!response) {
      throw new Error('No response data received');
    }

    if (!response.success) {
      // Use actual error message from API response
      const errorMessage = (response as unknown as { error?: string }).error
        || (apiResponse as unknown as { error?: string }).error
        || 'Failed to get passkey login options';
      throw new Error(errorMessage);
    }

    return response.data;
  } catch (error) {
    handleApiError(error);
  }
}

/**
 * Verify Passkey Login
 * Verifies the WebAuthn authentication assertion and returns tokens
 * Must be called after user completes biometric authentication
 */
export async function verifyPasskeyLogin(
  email: string,
  authenticationResponse: AuthenticationResponseJSON
): Promise<PasskeyLoginVerifyResponse> {
  try {
    // Generate device fingerprinting data for security
    const deviceFingerprintData = generateDeviceFingerprintData();

    // Validate fingerprint data has required UUID
    if (!deviceFingerprintData.fingerprint || typeof deviceFingerprintData.fingerprint !== 'string') {
      console.error('🔐 Passkey Login: Invalid fingerprint data - missing UUID');
      throw new Error('Device fingerprinting failed - missing UUID');
    }

    // Prepare enhanced headers for device fingerprinting
    const enhancedHeaders = {
      'X-Device-Fingerprint': JSON.stringify(deviceFingerprintData),
      'X-Incognito-Detected': deviceFingerprintData.incognitoDetected.toString(),
      'X-Canvas-Fingerprint': deviceFingerprintData.canvasFingerprint,
      'X-Device-Fingerprint-Version': deviceFingerprintData.fingerprintVersion,
      'X-LocalStorage-Available': deviceFingerprintData.localStorageAvailable.toString(),
      'X-SessionStorage-Available': deviceFingerprintData.sessionStorageAvailable.toString(),
      'X-IndexedDB-Available': deviceFingerprintData.indexedDBAvailable.toString(),
    };

    console.log('🔐 Passkey Login: Verifying authentication with device fingerprinting');

    const apiResponse = await apiClient.post<PasskeyLoginVerifyResponse>(
      userEndpoints.auth.passkeyLoginVerify(),
      { email, authenticationResponse },
      { headers: enhancedHeaders }
    );

    const response = apiResponse.data;

    if (!response) {
      throw new Error('No response data received');
    }

    return response;
  } catch (error) {
    handleApiError(error);
  }
}

/**
 * Get Passkey Registration Options
 * Retrieves WebAuthn registration challenge options
 * Requires authenticated user (adds passkey to existing account)
 */
export async function getPasskeyRegistrationOptions(
  accessToken: string
): Promise<PublicKeyCredentialCreationOptionsJSON> {
  try {
    console.log('🔐 Passkey Register: Getting registration options');

    const apiResponse = await apiClient.post<PasskeyRegistrationOptionsResponse>(
      userEndpoints.auth.passkeyRegisterOptions(),
      {},
      apiHelpers.withAuth(accessToken)
    );

    const response = apiResponse.data;

    if (!response) {
      throw new Error('No response data received');
    }

    if (!response.success) {
      throw new Error('Failed to get passkey registration options');
    }

    return response.data;
  } catch (error) {
    handleApiError(error);
  }
}

/**
 * Verify Passkey Registration
 * Verifies the WebAuthn registration credential and saves to account
 * Requires authenticated user
 */
export async function verifyPasskeyRegistration(
  accessToken: string,
  attestationResponse: RegistrationResponseJSON,
  friendlyName: string
): Promise<PasskeyRegistrationVerifyResponse> {
  try {
    console.log('🔐 Passkey Register: Verifying registration for', friendlyName);

    const apiResponse = await apiClient.post<PasskeyRegistrationVerifyResponse>(
      userEndpoints.auth.passkeyRegisterVerify(),
      { attestationResponse, friendlyName },
      apiHelpers.withAuth(accessToken)
    );

    const response = apiResponse.data;

    if (!response) {
      throw new Error('No response data received');
    }

    return response;
  } catch (error) {
    handleApiError(error);
  }
}

/**
 * List User Passkeys
 * Returns all registered passkeys for the authenticated user
 */
export async function listPasskeys(accessToken: string): Promise<Passkey[]> {
  try {
    console.log('🔐 Passkeys: Listing user passkeys');

    const apiResponse = await apiClient.get<ListPasskeysResponse>(
      userEndpoints.auth.passkeys(),
      undefined,
      apiHelpers.withAuth(accessToken)
    );

    const response = apiResponse.data;

    if (!response) {
      throw new Error('No response data received');
    }

    return response.data;
  } catch (error) {
    handleApiError(error);
  }
}

/**
 * Delete Passkey
 * Removes a registered passkey from the user's account
 * Requires at least one passkey or other auth method to remain
 */
export async function deletePasskey(accessToken: string, passkeyId: string): Promise<void> {
  try {
    console.log('🔐 Passkeys: Deleting passkey', passkeyId);

    await apiClient.delete<DeletePasskeyResponse>(
      userEndpoints.auth.passkeyDelete(passkeyId),
      apiHelpers.withAuth(accessToken)
    );
  } catch (error) {
    handleApiError(error);
  }
}

/**
 * Rename Passkey
 * Updates the friendly name of a registered passkey
 */
export async function renamePasskey(
  accessToken: string,
  passkeyId: string,
  friendlyName: string
): Promise<Passkey> {
  try {
    console.log('🔐 Passkeys: Renaming passkey', passkeyId, 'to', friendlyName);

    const apiResponse = await apiClient.patch<RenamePasskeyResponse>(
      userEndpoints.auth.passkeyRename(passkeyId),
      { friendlyName },
      apiHelpers.withAuth(accessToken)
    );

    const response = apiResponse.data;

    if (!response) {
      throw new Error('No response data received');
    }

    return response.data;
  } catch (error) {
    handleApiError(error);
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Phone OTP API Methods (Account Recovery)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Send Phone OTP for Account Recovery
 * Used when user cannot access their email
 * Requires email (to identify account) and phone (to verify ownership)
 */
export async function sendPhoneOtp(request: SendPhoneOtpRequest): Promise<SendPhoneOtpResponse> {
  try {
    // Generate device fingerprinting data for risk assessment
    const deviceFingerprintData = generateDeviceFingerprintData();

    // Validate fingerprint data has required UUID
    if (!deviceFingerprintData.fingerprint || typeof deviceFingerprintData.fingerprint !== 'string') {
      console.error('🔐 Send Phone OTP: Invalid fingerprint data - missing UUID');
      throw new Error('Device fingerprinting failed - missing UUID');
    }

    // Prepare enhanced headers for device fingerprinting
    const enhancedHeaders = {
      'X-Device-Fingerprint': JSON.stringify(deviceFingerprintData),
      'X-Incognito-Detected': deviceFingerprintData.incognitoDetected.toString(),
      'X-Canvas-Fingerprint': deviceFingerprintData.canvasFingerprint,
      'X-Device-Fingerprint-Version': deviceFingerprintData.fingerprintVersion,
      'X-LocalStorage-Available': deviceFingerprintData.localStorageAvailable.toString(),
      'X-SessionStorage-Available': deviceFingerprintData.sessionStorageAvailable.toString(),
      'X-IndexedDB-Available': deviceFingerprintData.indexedDBAvailable.toString(),
    };

    console.log('🔐 Send Phone OTP: Sending request with device fingerprinting');

    const apiResponse = await apiClient.post<SendPhoneOtpResponse>(
      userEndpoints.auth.phoneSend(),
      request,
      { headers: enhancedHeaders }
    );

    // Check if API client returned an error response
    if (apiResponse.success === false && apiResponse.data) {
      return apiResponse.data;
    }

    const response = apiResponse.data;

    if (!response) {
      throw new Error('No response data received');
    }

    return response;
  } catch (error) {
    handleApiError(error);
  }
}

/**
 * Verify Phone OTP
 * Verifies the 6-digit code sent to user's phone and returns tokens on success
 * This completes account recovery and logs the user in
 */
export async function verifyPhoneOtp(request: VerifyPhoneOtpRequest): Promise<VerifyPhoneOtpResponse> {
  try {
    // Generate device fingerprinting data for security
    const deviceFingerprintData = generateDeviceFingerprintData();

    // Validate fingerprint data has required UUID
    if (!deviceFingerprintData.fingerprint || typeof deviceFingerprintData.fingerprint !== 'string') {
      console.error('🔐 Verify Phone OTP: Invalid fingerprint data - missing UUID');
      throw new Error('Device fingerprinting failed - missing UUID');
    }

    // Prepare enhanced headers for device fingerprinting
    const enhancedHeaders = {
      'X-Device-Fingerprint': JSON.stringify(deviceFingerprintData),
      'X-Incognito-Detected': deviceFingerprintData.incognitoDetected.toString(),
      'X-Canvas-Fingerprint': deviceFingerprintData.canvasFingerprint,
      'X-Device-Fingerprint-Version': deviceFingerprintData.fingerprintVersion,
      'X-LocalStorage-Available': deviceFingerprintData.localStorageAvailable.toString(),
      'X-SessionStorage-Available': deviceFingerprintData.sessionStorageAvailable.toString(),
      'X-IndexedDB-Available': deviceFingerprintData.indexedDBAvailable.toString(),
    };

    console.log('🔐 Verify Phone OTP: Verifying code with device fingerprinting');

    const apiResponse = await apiClient.post<VerifyPhoneOtpResponse>(
      userEndpoints.auth.phoneVerify(),
      request,
      { headers: enhancedHeaders }
    );

    // Check if API client returned an error response
    if (apiResponse.success === false && apiResponse.data) {
      return apiResponse.data as VerifyPhoneOtpResponse;
    }

    const response = apiResponse.data;

    if (!response) {
      throw new Error('No response data received');
    }

    return response;
  } catch (error) {
    handleApiError(error);
  }
}

/**
 * Social Login
 * Verifies Google or Apple ID token with backend and returns user + tokens
 * Backend verifies token cryptographically; never trust client-provided user IDs
 */
export async function socialLogin(request: SocialAuthRequest): Promise<SocialAuthResponse> {
  try {
    const deviceFingerprintData = generateDeviceFingerprintData();

    if (!deviceFingerprintData.fingerprint || typeof deviceFingerprintData.fingerprint !== 'string') {
      console.error('🔐 Social Login: Invalid fingerprint data - missing UUID');
      throw new Error('Device fingerprinting failed - missing UUID');
    }

    const enhancedHeaders = {
      'X-Device-Fingerprint': JSON.stringify(deviceFingerprintData),
      'X-Incognito-Detected': deviceFingerprintData.incognitoDetected.toString(),
      'X-Canvas-Fingerprint': deviceFingerprintData.canvasFingerprint,
      'X-Device-Fingerprint-Version': deviceFingerprintData.fingerprintVersion,
      'X-LocalStorage-Available': deviceFingerprintData.localStorageAvailable.toString(),
      'X-SessionStorage-Available': deviceFingerprintData.sessionStorageAvailable.toString(),
      'X-IndexedDB-Available': deviceFingerprintData.indexedDBAvailable.toString(),
    };

    const apiResponse = await apiClient.post<SocialAuthResponse>(
      userEndpoints.auth.social(),
      request,
      { headers: enhancedHeaders }
    );

    if (apiResponse.success === false && apiResponse.data) {
      return apiResponse.data as SocialAuthResponse;
    }

    const response = apiResponse.data;

    if (!response) {
      throw new Error('No response data received');
    }

    return response;
  } catch (error) {
    handleApiError(error);
  }
}

/**
 * User Authentication API Service Object
 * Clean, simple interface - just call the methods
 * No URL construction, no manual error handling
 */
export const userAuthApi = {
  // Legacy password-based auth
  login: userLogin,
  register: userRegister,
  logout: userLogout,
  refresh: refreshUserToken,
  getProfile: getUserProfile,
  createAnonymous: createAnonymousSession,
  verifyEmail: verifyUserEmail,
  getEmailVerificationStatus,
  resendVerificationEmail,
  forgotPassword: forgotUserPassword,
  verifyResetCode: verifyUserResetCode,
  resetPassword: resetUserPassword,
  verifyTwoFactor: verifyUserTwoFactor,
  resendTwoFactor: resendUserTwoFactor,
  validateToken: validateUserToken,
  // Email OTP (Primary auth method)
  sendOtp: sendEmailOtp,
  verifyOtp: verifyEmailOtp,
  // Passkey/WebAuthn (Secondary auth method)
  getPasskeyLoginOptions,
  verifyPasskeyLogin,
  getPasskeyRegistrationOptions,
  verifyPasskeyRegistration,
  listPasskeys,
  deletePasskey,
  renamePasskey,
  // Phone OTP (Account recovery)
  sendPhoneOtp,
  verifyPhoneOtp,
  // Social login (Google, Apple)
  socialLogin,
} as const;
